import { useState } from "react";
import { toast } from "sonner";
import { useSWRConfig } from "swr";
import { CreateSubAgentRequest, UpdateSubAgentRequest } from "@/services/agent/agent.api.types";
import { AgentService } from "@/services/agent/agent.service";

export const useSubAgentMutations = () => {
  const { mutate } = useSWRConfig();
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const invalidateSubAgentCaches = (subAgentId?: string) => {
    // サブエージェント一覧のキャッシュを無効化（全てのworkspaceIdとenabledOnlyの組み合わせ）
    mutate(
      (key) => Array.isArray(key) && key[0] === "agent" && key[1] === "subAgents",
      undefined,
      { revalidate: true },
    );

    // 個別のサブエージェントキャッシュを無効化（subAgentIdが指定されている場合）
    if (subAgentId) {
      mutate(["agent", "subAgent", subAgentId], undefined, { revalidate: true });
    }
  };

  const createSubAgent = async (data: CreateSubAgentRequest) => {
    setIsCreating(true);
    try {
      const result = await AgentService.createSubAgent(data);
      toast.success("サブエージェントを作成しました");
      invalidateSubAgentCaches();
      return result;
    } catch (error) {
      console.error("Failed to create sub agent:", error);
      toast.error("サブエージェントの作成に失敗しました");
      throw error;
    } finally {
      setIsCreating(false);
    }
  };

  const updateSubAgent = async (subAgentId: string, data: UpdateSubAgentRequest) => {
    setIsUpdating(true);
    try {
      const result = await AgentService.updateSubAgent(subAgentId, data);
      toast.success("サブエージェントを更新しました");
      invalidateSubAgentCaches(subAgentId);
      return result;
    } catch (error) {
      console.error("Failed to update sub agent:", error);
      toast.error("サブエージェントの更新に失敗しました");
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteSubAgent = async (subAgentId: string, subAgentName: string) => {
    if (!confirm(`サブエージェント「${subAgentName}」を削除してもよろしいですか？`)) {
      return;
    }

    setIsDeleting(true);
    try {
      await AgentService.deleteSubAgent(subAgentId);
      toast.success("サブエージェントを削除しました");
      invalidateSubAgentCaches(subAgentId);
    } catch (error) {
      console.error("Failed to delete sub agent:", error);
      toast.error("サブエージェントの削除に失敗しました");
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    createSubAgent,
    updateSubAgent,
    deleteSubAgent,
    isCreating,
    isUpdating,
    isDeleting,
  };
};
